import { useEffect, useRef, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useForm } from "../../context/FormContext";
import type { FormData } from "../../context/FormContext";
import {
  getPhoneLengthRule,
  isValidOptionalPhoneParts,
  isValidRequiredPhoneParts,
} from "../../utils/contactFieldValidation";
import { splitDialNationalFromStored } from "../../utils/splitDialNational";
import ButtonChoice from "../ui/ButtonChoice";
import PhoneDialNationalInput from "../ui/PhoneDialNationalInput";
import RepresentativePhoneOtpField from "./RepresentativePhoneOtpField";
import { capitalizeWords, isStrictEmail } from "../../../utils/formInputUtils";

interface EditableFieldProps {
  fieldKey: keyof FormData;
  label: string;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  helper?: string;
  isOptional?: boolean;
  optionalLabel?: string;
}

function ReadOnlyField({ fieldKey, label, placeholder, type = "text", maxLength, isOptional, optionalLabel }: EditableFieldProps) {
  const { data, update } = useForm();
  const value = (data[fieldKey] as string) ?? "";
  const [editing, setEditing] = useState(false);
  const editRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node | null;
      if (!editRootRef.current || !t || !editRootRef.current.contains(t)) {
        setEditing(false);
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("focusin", onFocusIn);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [editing]);

  const isEmail = type === "email";
  const emailValid = isEmail && isStrictEmail(value);

  const normalizeFieldValue = (next: string): string => {
    if (fieldKey === "nie") return next.toUpperCase().slice(0, 9);
    if (fieldKey === "firstName" || fieldKey === "primerApellido" || fieldKey === "applicantFirstName" || fieldKey === "applicantPrimerApellido") {
      return capitalizeWords(next);
    }
    return next;
  };

  if (editing) {
    return (
      <div ref={editRootRef} className="py-3 border-b border-slate-50 last:border-0">
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">{label}</label>
        </div>
        <div className="flex gap-2">
          <input
            id={`field-${String(fieldKey)}`}
            autoFocus
            type={type}
            value={value}
            onChange={(e) => update(fieldKey, normalizeFieldValue(e.target.value))}
            placeholder={placeholder}
            maxLength={maxLength}
            style={{ fontSize: "16px" }}
            className="flex-1 h-12 px-4 bg-white border border-emerald-400 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="h-12 w-12 flex items-center justify-center bg-emerald-600 text-white rounded-xl cursor-pointer hover:bg-emerald-700 transition-colors shrink-0"
            aria-label={`Save ${label}`}
          >
            <i className="ri-check-line text-lg" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      id={`field-${String(fieldKey)}`}
      type="button"
      onClick={() => setEditing(true)}
      className="flex w-full items-center justify-between py-3.5 border-b border-slate-50 last:border-0 text-left cursor-pointer rounded-lg -mx-1 px-1 hover:bg-slate-50/80 transition-colors"
      aria-label={`${label}: edit`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[12px] text-slate-400 uppercase tracking-wide font-semibold">{label}</p>
        <div className="flex items-center gap-1.5">
          <p className={`text-[15px] mt-0.5 font-medium ${value ? "text-slate-900" : "text-slate-400"}`}>
            {value || ""}
          </p>
          {isEmail && value && (
            emailValid ? (
              <i className="ri-checkbox-circle-fill text-emerald-500 text-sm mt-0.5 shrink-0" />
            ) : (
              <i className="ri-error-warning-line text-amber-400 text-sm mt-0.5 shrink-0" />
            )
          )}
        </div>
      </div>
      <span
        className="ml-4 w-9 h-9 flex items-center justify-center rounded-full shrink-0 pointer-events-none"
        aria-hidden
      >
        <i className="ri-pencil-line text-slate-400 text-base" />
      </span>
    </button>
  );
}

function EditableField({ fieldKey, label, placeholder, type = "text", maxLength, helper, isOptional, optionalLabel }: EditableFieldProps) {
  const { data, update } = useForm();
  const value = (data[fieldKey] as string) ?? "";
  const normalizeFieldValue = (next: string): string => {
    if (fieldKey === "nie") return next.toUpperCase().slice(0, 9);
    if (fieldKey === "firstName" || fieldKey === "primerApellido" || fieldKey === "applicantFirstName" || fieldKey === "applicantPrimerApellido") {
      return capitalizeWords(next);
    }
    return next;
  };

  const isEmail = type === "email";
  const emailValid = isEmail && isStrictEmail(value);

  return (
    <div className="py-3 border-b border-slate-50 last:border-0">
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">{label}</label>
        {isOptional && optionalLabel && (
          <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">{optionalLabel}</span>
        )}
      </div>
      <div className="relative">
        <input
          id={`field-${String(fieldKey)}`}
          type={type}
          value={value}
          onChange={(e) => update(fieldKey, normalizeFieldValue(e.target.value))}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{ fontSize: "16px" }}
          className={`w-full h-12 px-4 ${isEmail ? "pr-10" : ""} bg-white border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all ${
            value
              ? isEmail
                ? emailValid ? "border-emerald-400" : "border-amber-400"
                : "border-emerald-400"
              : "border-slate-200 focus:border-transparent"
          }`}
        />
        {isEmail && value && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {emailValid
              ? <i className="ri-checkbox-circle-fill text-emerald-500" />
              : <i className="ri-error-warning-line text-amber-400" />}
          </div>
        )}
      </div>
      {helper && <p className="text-[13px] text-slate-400 mt-1">{helper}</p>}
      {isEmail && value && !emailValid ? <p className="text-[12px] text-rose-500 mt-1">Please enter a valid email address.</p> : null}
    </div>
  );
}

function ApplicantPhoneField({
  label,
  placeholder,
  optionalLabel,
  readOnly,
}: {
  label: string;
  placeholder: string;
  optionalLabel: string;
  readOnly: boolean;
}) {
  const { data, update } = useForm();
  const [editing, setEditing] = useState(false);
  const editRootRef = useRef<HTMLDivElement>(null);
  const code = (data.applicantPhoneCountryCode as string) || "+34";
  const national = (data.applicantPhone as string) || "";
  const display = national.trim() ? `${code} ${national}`.trim() : "";
  const phoneValid = isValidOptionalPhoneParts(code, national);
  const { min: minDigits, max: maxDigits } = getPhoneLengthRule(code);

  useEffect(() => {
    if (!readOnly || !editing) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node | null;
      if (!editRootRef.current || !t || !editRootRef.current.contains(t)) {
        setEditing(false);
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("focusin", onFocusIn);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [readOnly, editing]);

  if (readOnly && !editing) {
    return (
      <button
        id="field-applicantPhone"
        type="button"
        onClick={() => setEditing(true)}
        className="flex w-full items-center justify-between py-3.5 border-b border-slate-50 last:border-0 text-left cursor-pointer rounded-lg -mx-1 px-1 hover:bg-slate-50/80 transition-colors"
        aria-label={`${label}: edit`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <p className="text-[12px] text-slate-400 uppercase tracking-wide font-semibold">{label}</p>
            <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
              {optionalLabel}
            </span>
          </div>
          <p
            className={`text-[15px] mt-0.5 font-medium break-words ${display ? "text-slate-900" : "text-slate-400"}`}
          >
            {display || ""}
          </p>
        </div>
        <span
          className="ml-4 w-9 h-9 flex items-center justify-center rounded-full shrink-0 pointer-events-none"
          aria-hidden
        >
          <i className="ri-pencil-line text-slate-400 text-base" />
        </span>
      </button>
    );
  }

  if (readOnly && editing) {
    return (
      <div ref={editRootRef} className="py-3 border-b border-slate-50 last:border-0" id="field-applicantPhone">
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">{label}</label>
        </div>
        <div className="flex gap-2 items-stretch">
          <div className="flex-1 min-w-0">
            <PhoneDialNationalInput
              countryCode={code}
              onCountryCodeChange={(c) => update("applicantPhoneCountryCode", c)}
              nationalNumber={national}
              onNationalNumberChange={(v) => update("applicantPhone", v)}
              placeholder={placeholder}
            />
            {!phoneValid ? (
              <p className="text-[12px] text-rose-500 mt-1">
                Enter between {minDigits} and {maxDigits} digits for this country.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              if (!phoneValid) return;
              setEditing(false);
            }}
            className={`h-12 w-12 flex items-center justify-center text-white rounded-xl transition-colors shrink-0 self-end ${
              phoneValid ? "bg-emerald-600 cursor-pointer hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"
            }`}
            aria-label={`Save ${label}`}
            disabled={!phoneValid}
          >
            <i className="ri-check-line text-lg" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-3 border-b border-slate-50 last:border-0" id="field-applicantPhone">
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">{label}</label>
        <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
          {optionalLabel}
        </span>
      </div>
      <PhoneDialNationalInput
        countryCode={code}
        onCountryCodeChange={(c) => update("applicantPhoneCountryCode", c)}
        nationalNumber={national}
        onNationalNumberChange={(v) => update("applicantPhone", v)}
        placeholder={placeholder}
      />
    </div>
  );
}

function RepresentativePhoneField({
  label,
  placeholder,
}: {
  label: string;
  placeholder: string;
}) {
  const { data, update } = useForm();
  const [editing, setEditing] = useState(false);
  const editRootRef = useRef<HTMLDivElement>(null);
  const split = splitDialNationalFromStored(
    String(data.phone || ""),
    String(data.phoneCountryCode || "+34")
  );
  const code = split.dial || "+34";
  const national = split.national || "";
  const hasValue = national.trim().length > 0;
  const phoneValid = isValidRequiredPhoneParts(code, national);
  const { min: minDigits, max: maxDigits } = getPhoneLengthRule(code);

  useEffect(() => {
    if (!editing) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node | null;
      if (!editRootRef.current || !t || !editRootRef.current.contains(t)) {
        setEditing(false);
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("focusin", onFocusIn);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [editing]);

  if (editing) {
    return (
      <div ref={editRootRef} className="py-3 border-b border-slate-50 last:border-0" id="field-phone">
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">{label}</label>
        </div>
        <div className="flex gap-2 items-stretch">
          <div className="flex-1 min-w-0">
            <PhoneDialNationalInput
              countryCode={code}
              onCountryCodeChange={(c) => update("phoneCountryCode", c)}
              nationalNumber={national}
              onNationalNumberChange={(v) => update("phone", v)}
              placeholder={placeholder}
            />
            {!phoneValid && national.trim() ? (
              <p className="text-[12px] text-rose-500 mt-1">
                Enter between {minDigits} and {maxDigits} digits for this country.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              if (!phoneValid) return;
              setEditing(false);
            }}
            className={`h-12 w-12 flex items-center justify-center text-white rounded-xl transition-colors shrink-0 self-end ${
              phoneValid ? "bg-emerald-600 cursor-pointer hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"
            }`}
            aria-label={`Save ${label}`}
            disabled={!phoneValid}
          >
            <i className="ri-check-line text-lg" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      id="field-phone"
      type="button"
      onClick={() => setEditing(true)}
      className="flex w-full items-center justify-between py-3.5 border-b border-slate-50 last:border-0 text-left cursor-pointer rounded-lg -mx-1 px-1 hover:bg-slate-50/80 transition-colors"
      aria-label={`${label}: edit`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[12px] text-slate-400 uppercase tracking-wide font-semibold">{label}</p>
        <p className={`text-[15px] mt-0.5 font-medium break-words ${hasValue ? "text-slate-900" : "text-slate-400"}`}>
          {hasValue ? `${code} ${national}` : ""}
        </p>
        {hasValue && !phoneValid ? (
          <p className="text-[12px] text-rose-500 mt-1">
            Enter between {minDigits} and {maxDigits} digits for this country.
          </p>
        ) : null}
      </div>
      <span className="ml-4 w-9 h-9 flex items-center justify-center rounded-full shrink-0 pointer-events-none" aria-hidden>
        <i className="ri-pencil-line text-slate-400 text-base" />
      </span>
    </button>
  );
}

function NieField({
  label,
  helper,
}: {
  label: string;
  helper: string;
}) {
  const { data, update } = useForm();
  const value = String(data.nie || "");
  const hasValue = value.trim().length > 0;
  const [editing, setEditing] = useState(!hasValue);
  const editRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasValue) {
      setEditing(true);
    }
  }, [hasValue]);

  useEffect(() => {
    if (!editing || !hasValue) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node | null;
      if (!editRootRef.current || !t || !editRootRef.current.contains(t)) {
        setEditing(false);
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("focusin", onFocusIn);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [editing, hasValue]);

  if (!editing && hasValue) {
    return (
      <button
        id="field-nie"
        type="button"
        onClick={() => setEditing(true)}
        className="flex w-full items-center justify-between py-3.5 border-b border-slate-50 last:border-0 text-left cursor-pointer rounded-lg -mx-1 px-1 hover:bg-slate-50/80 transition-colors"
        aria-label={`${label}: edit`}
      >
        <div className="min-w-0 flex-1">
          <p className="text-[12px] text-slate-400 uppercase tracking-wide font-semibold">{label}</p>
          <p className="text-[15px] mt-0.5 font-medium text-slate-900">{value}</p>
          <p className="text-[13px] text-slate-400 mt-1">{helper}</p>
        </div>
        <span className="ml-4 w-9 h-9 flex items-center justify-center rounded-full shrink-0 pointer-events-none" aria-hidden>
          <i className="ri-pencil-line text-slate-400 text-base" />
        </span>
      </button>
    );
  }

  return (
    <div
      ref={editRootRef}
      id="field-nie"
      className={`px-0 py-3 transition-colors border-b border-slate-50 last:border-0 ${!hasValue ? "bg-amber-50" : ""}`}
    >
      <label className="block text-[13px] font-medium text-slate-500 mb-1">
        {label}
      </label>
      <input
        type="text"
        autoFocus
        value={value}
        onChange={(e) => update("nie", e.target.value)}
        placeholder="X1234567A"
        style={{ fontSize: "16px" }}
        className={`block w-full h-auto rounded-none border-0 border-b bg-transparent p-0 pb-0.5 text-[15px] text-slate-800 shadow-none outline-none ring-0 transition-colors focus:outline-none focus:ring-0 ${
          hasValue ? "border-emerald-400" : "border-amber-300"
        } placeholder:text-slate-400`}
      />
      <p className="text-[13px] text-slate-400 mt-1">{helper}</p>
    </div>
  );
}

export default function ConfirmDetailsSection() {
  const { t } = useLang();
  const { data, update } = useForm();
  const isRep = data.registrantType === "representative";
  const optionalLabel = t("optional");
  const contactAssignedTo = data.contactAssignedTo;

  useEffect(() => {
    if (!data.registrantType) {
      update("registrantType", "individual");
    }
    if (!data.contactAssignedTo) {
      update("contactAssignedTo", "rep");
    }
  }, [data.contactAssignedTo, data.registrantType, update]);

  const moveContactToApplicant = () => {
    const repFirst = (data.firstName as string) ?? "";
    const repPrimer = (data.primerApellido as string) ?? "";
    const repSegundo = (data.segundoApellido as string) ?? "";
    const repEmail = (data.email as string) ?? "";
    const repPhone = (data.phone as string) ?? "";
    const repPhoneCode = (data.phoneCountryCode as string) ?? "+34";
    const repSplit = splitDialNationalFromStored(repPhone, repPhoneCode);

    update("applicantFirstName", repFirst);
    update("applicantPrimerApellido", repPrimer);
    update("applicantSegundoApellido", repSegundo);
    update("applicantEmail", repEmail);
    update("applicantPhone", repSplit.national);
    update("applicantPhoneCountryCode", repSplit.dial);

    update("firstName", "");
    update("primerApellido", "");
    update("segundoApellido", "");
    update("email", "");
    update("phone", "");
    update("phoneCountryCode", repSplit.dial);

    update("contactAssignedTo", "applicant");
    update("repPhoneOtpVerified", false);
  };

  const moveContactToRep = () => {
    const appFirst = (data.applicantFirstName as string) ?? "";
    const appPrimer = (data.applicantPrimerApellido as string) ?? "";
    const appSegundo = (data.applicantSegundoApellido as string) ?? "";
    const appEmail = (data.applicantEmail as string) ?? "";
    const appPhone = (data.applicantPhone as string) ?? "";
    const appPhoneCode = (data.applicantPhoneCountryCode as string) ?? "+34";
    const appSplit = splitDialNationalFromStored(appPhone, appPhoneCode);

    update("firstName", appFirst);
    update("primerApellido", appPrimer);
    update("segundoApellido", appSegundo);
    update("email", appEmail);
    update("phone", appSplit.national);
    update("phoneCountryCode", appSplit.dial);

    update("applicantFirstName", "");
    update("applicantPrimerApellido", "");
    update("applicantSegundoApellido", "");
    update("applicantEmail", "");
    update("applicantPhone", "");
    update("applicantPhoneCountryCode", "+34");

    update("contactAssignedTo", "rep");
    update("repPhoneOtpVerified", true);
  };

  return (
    <div className="max-w-[480px] mx-auto px-4 pt-6 pb-2">
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-md bg-emerald-600 shrink-0" />
          <h1 className="text-xl font-display text-slate-900 leading-tight">
            {isRep ? t("section1.voiceTitle_rep") : t("section1.voiceTitle")}
          </h1>
        </div>
      </div>

      {isRep && (
        <p className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide mb-2 px-1">
          {t("section1.repSectionLabel") || "Representative"}
        </p>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 px-4 mb-5">
        {contactAssignedTo === "applicant" && isRep ? (
          <>
            <EditableField fieldKey="firstName" label={t("section1.firstName")} placeholder={t("section1.firstNamePlaceholder")} maxLength={40} />
            <EditableField fieldKey="primerApellido" label={t("section1.lastName")} placeholder={t("section1.lastNamePlaceholder")} maxLength={40} />
            <EditableField fieldKey="segundoApellido" label={t("section1.segundoApellido")} placeholder={t("section1.segundoApellidoPlaceholder")} maxLength={40} isOptional optionalLabel={optionalLabel} />
          </>
        ) : (
          <>
            <ReadOnlyField fieldKey="firstName" label={t("section1.firstName")} placeholder={t("section1.firstNamePlaceholder")} maxLength={40} />
            <ReadOnlyField fieldKey="primerApellido" label={t("section1.lastName")} placeholder={t("section1.lastNamePlaceholder")} maxLength={40} />
            <ReadOnlyField fieldKey="segundoApellido" label={t("section1.segundoApellido")} placeholder={t("section1.segundoApellidoPlaceholder")} maxLength={40} isOptional optionalLabel={optionalLabel} />
            {!isRep ? <NieField label={t("section6.nie")} helper={t("section6.nieHelper")} /> : null}
          </>
        )}

        <div className="border-t border-slate-100">
          {[
            {
              label: t("section3.fatherName"),
              fnKey: "fatherFirstName" as const,
              unkKey: "fatherUnknown" as const,
              unkLabel: t("section3.fatherUnknown"),
            },
            {
              label: t("section3.motherName"),
              fnKey: "motherFirstName" as const,
              unkKey: "motherUnknown" as const,
              unkLabel: t("section3.motherUnknown"),
            },
          ].map((parent, idx) => {
            const isUnknown = data[parent.unkKey] === "yes";
            const parentValue = (data[parent.fnKey] as string) ?? "";
            const needsAttention = !isUnknown && !parentValue;
            return (
              <div
                key={parent.fnKey}
                id={`field-${String(parent.fnKey)}`}
                className={`px-0 py-3 transition-colors ${idx > 0 ? "border-t border-slate-100" : ""} ${
                  needsAttention ? "bg-amber-50" : ""
                }`}
              >
                <label className="block text-[13px] font-medium text-slate-500 mb-1">
                  {parent.label}
                </label>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {!isUnknown && (
                      <input
                        type="text"
                        value={parentValue}
                        data-field={parent.fnKey}
                        onChange={(e) => update(parent.fnKey, e.target.value)}
                        placeholder={t("section3.fatherFirst")}
                        style={{ fontSize: "16px" }}
                        className={`block w-full h-auto rounded-none border-0 border-b bg-transparent p-0 pb-0.5 text-[15px] text-slate-800 shadow-none outline-none ring-0 transition-colors focus:outline-none focus:ring-0 ${
                          parentValue ? "border-emerald-400" : "border-amber-300"
                        } placeholder:text-slate-400`}
                      />
                    )}
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer shrink-0 whitespace-nowrap">
                    <input
                      type="checkbox"
                      tabIndex={-1}
                      checked={isUnknown}
                      onChange={(e) => {
                        update(parent.unkKey, e.target.checked ? "yes" : "");
                        if (e.target.checked) update(parent.fnKey, "");
                      }}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                    {parent.unkLabel}
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-100 px-0 py-3">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-3">
            {t("section3.civilStatus")}
          </p>
          <div
            id="field-civilStatus"
            className={`grid grid-cols-3 gap-2 rounded-xl p-2 -mx-2 transition-colors ${
              !data.civilStatus ? "bg-amber-50 border border-amber-200" : ""
            }`}
            data-field="civilStatus"
          >
            {[
              { value: "single", labelKey: "section3.civilOptions.single" },
              { value: "married", labelKey: "section3.civilOptions.married" },
              { value: "widowed", labelKey: "section3.civilOptions.widowed" },
              { value: "divorced", labelKey: "section3.civilOptions.divorced" },
              { value: "separated", labelKey: "section3.civilOptions.separated" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => update("civilStatus", opt.value as typeof data.civilStatus)}
                className={[
                  "px-3 py-2 rounded-lg text-[13px] font-medium transition-all border whitespace-nowrap",
                  data.civilStatus === opt.value
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-100",
                ].join(" ")}
              >
                {t(opt.labelKey as Parameters<typeof t>[0])}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 px-4 mb-5">
        {contactAssignedTo === "applicant" && isRep ? (
          <>
            <EditableField fieldKey="email" label={t("section1.email")} placeholder={t("section1.emailPlaceholder")} type="email" maxLength={100} />
            <div id="field-repPhoneOtpVerified">
              <RepresentativePhoneOtpField />
            </div>
          </>
        ) : (
          <>
            <ReadOnlyField fieldKey="email" label={t("section1.email")} placeholder={t("section1.emailPlaceholder")} type="email" maxLength={100} />
            <RepresentativePhoneField label={t("section1.phone")} placeholder={t("section1.phonePlaceholder")} />
          </>
        )}
      </div>

      {isRep && contactAssignedTo === "rep" && (
        <div className="mb-5 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3.5 flex items-start gap-3">
          <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-500 text-base" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-amber-800 font-medium leading-snug">
              Account details assigned to you (the representative)
            </p>
            <p className="text-[12px] text-amber-700 mt-1 leading-snug">
              If this account was registered by the applicant themselves, move the contact info to the applicant section below, then fill in your own details above.
            </p>
            <button
              type="button"
              onClick={moveContactToApplicant}
              className="mt-2.5 text-[12px] font-semibold text-amber-800 underline underline-offset-2 cursor-pointer hover:text-amber-900 transition-colors whitespace-nowrap"
            >
              Move to applicant section
            </button>
          </div>
        </div>
      )}

      {data.registrantType === "representative" && (
        <>
          <div className="bg-white rounded-2xl border border-slate-100 px-4 mb-5">
            <div id="field-repRelationship" className="py-3 border-b border-slate-50">
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                {t("section1.repRelationship")}
              </label>
              <select
                value={data.repRelationship as string}
                onChange={(e) => update("repRelationship", e.target.value)}
                className="w-full text-[15px] text-slate-800 bg-transparent border-none outline-none cursor-pointer"
              >
                <option value="">{t("section1.repRelSelectPlaceholder")}</option>
                <option value="family">{t("section1.repRelFamily")}</option>
                <option value="spouse">{t("section1.repRelSpouse")}</option>
                <option value="friend">{t("section1.repRelFriend")}</option>
                <option value="socialworker">{t("section1.repRelSocialWorker")}</option>
                <option value="lawyer">{t("section1.repRelLawyer")}</option>
                <option value="ngo">{t("section1.repRelNGO")}</option>
                <option value="other">{t("section1.repRelOther")}</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3 px-1">
            <p className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">
              {t("section1.applicantTitle")}
            </p>
            {contactAssignedTo === "applicant" && (
              <button
                type="button"
                onClick={moveContactToRep}
                className="flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line text-[11px]" />
                Undo move back to representative
              </button>
            )}
          </div>

          {contactAssignedTo === "applicant" && (
            <div className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 flex items-center gap-2.5">
              <i className="ri-check-line text-emerald-500 text-sm shrink-0" />
              <p className="text-[12px] text-emerald-700 leading-snug">
                Account contact details moved here. Edit the representative fields above to add their own contact info.
              </p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 px-4 mb-5">
            {contactAssignedTo === "applicant" ? (
              <>
                <ReadOnlyField fieldKey="applicantFirstName" label={t("section1.applicantFirstName")} placeholder={t("section1.applicantFirstNamePlaceholder")} maxLength={40} />
                <ReadOnlyField fieldKey="applicantPrimerApellido" label={t("section1.applicantLastName")} placeholder={t("section1.applicantLastNamePlaceholder")} maxLength={40} />
                <ReadOnlyField fieldKey="applicantSegundoApellido" label={t("section1.applicantSegundoApellido")} placeholder={t("section1.segundoApellidoPlaceholder")} maxLength={40} isOptional optionalLabel={optionalLabel} />
                <NieField label={t("section6.nie")} helper={t("section6.nieHelper")} />
              </>
            ) : (
              <>
                <EditableField fieldKey="applicantFirstName" label={t("section1.applicantFirstName")} placeholder={t("section1.applicantFirstNamePlaceholder")} maxLength={40} />
                <EditableField fieldKey="applicantPrimerApellido" label={t("section1.applicantLastName")} placeholder={t("section1.applicantLastNamePlaceholder")} maxLength={40} />
                <EditableField fieldKey="applicantSegundoApellido" label={t("section1.applicantSegundoApellido")} placeholder={t("section1.segundoApellidoPlaceholder")} maxLength={40} isOptional optionalLabel={optionalLabel} />
                <NieField label={t("section6.nie")} helper={t("section6.nieHelper")} />
              </>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 px-4 mb-8">
            {contactAssignedTo === "applicant" ? (
              <>
                <ReadOnlyField fieldKey="applicantEmail" label={t("section1.applicantEmail")} placeholder={t("section1.applicantEmailPlaceholder")} type="email" maxLength={100} isOptional optionalLabel={optionalLabel} />
                <ApplicantPhoneField
                  readOnly
                  label={t("section1.applicantPhone")}
                  placeholder={t("section1.applicantPhonePlaceholder")}
                  optionalLabel={optionalLabel}
                />
              </>
            ) : (
              <>
                <EditableField fieldKey="applicantEmail" label={t("section1.applicantEmail")} placeholder={t("section1.applicantEmailPlaceholder")} type="email" maxLength={100} isOptional optionalLabel={optionalLabel} />
                <ApplicantPhoneField
                  readOnly={false}
                  label={t("section1.applicantPhone")}
                  placeholder={t("section1.applicantPhonePlaceholder")}
                  optionalLabel={optionalLabel}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
